// Onboarding Redux slice — reducer + action types
// State is per-user, persisted via localStorage keyed by userId

// Action types
export const ONBOARDING_LOAD = 'ONBOARDING_LOAD';
export const ONBOARDING_COMPLETE_TOUR = 'ONBOARDING_COMPLETE_TOUR';
export const ONBOARDING_MARK_ALL_COMPLETED = 'ONBOARDING_MARK_ALL_COMPLETED';
export const ONBOARDING_SET_ACTIVE_TOUR = 'ONBOARDING_SET_ACTIVE_TOUR';
export const ONBOARDING_RESET = 'ONBOARDING_RESET';

const getInitialTours = () => ({
  dashboard: { completed: false, completedAt: null },
  search: { completed: false, completedAt: null },
  chat: { completed: false, completedAt: null },
  contract: { completed: false, completedAt: null },
  legalNews: { completed: false, completedAt: null },
  annonceLegale: { completed: false, completedAt: null },
  settings: { completed: false, completedAt: null },
});

const initialState = {
  loaded: false,
  userId: null,
  tours: getInitialTours(),
  allCompleted: false,
  skippedAt: null,
  activeTour: null,
};

// localStorage helpers
const STORAGE_PREFIX = '_lw_onboarding_';

function loadFromStorage(userId) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + userId);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveToStorage(userId, state) {
  try {
    localStorage.setItem(
      STORAGE_PREFIX + userId,
      JSON.stringify({
        tours: state.tours,
        allCompleted: state.allCompleted,
        skippedAt: state.skippedAt,
      })
    );
  } catch {
    // Storage full or unavailable
  }
}

const onboardingReducer = (state = initialState, action) => {
  let newState;

  switch (action.type) {
    case ONBOARDING_LOAD: {
      const { userId } = action.payload;
      const saved = loadFromStorage(userId);
      if (saved) {
        return {
          ...state,
          loaded: true,
          userId,
          tours: { ...getInitialTours(), ...saved.tours },
          allCompleted: saved.allCompleted || false,
          skippedAt: saved.skippedAt || null,
          activeTour: null,
        };
      }
      return {
        ...initialState,
        loaded: true,
        userId,
      };
    }

    case ONBOARDING_COMPLETE_TOUR: {
      const { tourId } = action.payload;
      const updatedTours = {
        ...state.tours,
        [tourId]: {
          completed: true,
          completedAt: new Date().toISOString(),
        },
      };
      const allDone = Object.values(updatedTours).every((t) => t.completed);
      newState = {
        ...state,
        tours: updatedTours,
        allCompleted: allDone,
        activeTour: null,
      };
      if (state.userId) saveToStorage(state.userId, newState);
      return newState;
    }

    case ONBOARDING_MARK_ALL_COMPLETED:
      newState = {
        ...state,
        allCompleted: true,
        skippedAt: new Date().toISOString(),
        activeTour: null,
      };
      if (state.userId) saveToStorage(state.userId, newState);
      return newState;

    case ONBOARDING_SET_ACTIVE_TOUR:
      return {
        ...state,
        activeTour: action.payload,
      };

    case ONBOARDING_RESET:
      if (state.userId) {
        localStorage.removeItem(STORAGE_PREFIX + state.userId);
      }
      return { ...initialState };

    default:
      return state;
  }
};

export default onboardingReducer;
